import { Client, OneDriveLargeFileUploadTask } from '@microsoft/microsoft-graph-client';
import {IAuthenticationProvider} from './interfaces/IAuthenticationProvider'
import * as GraphClientInitOptions from './ms-graph/GraphClientInitOptions'
import { IOneDriveItem } from './interfaces/IOneDriveItem'
import { LogEvents } from '../utils/LogEvents';
import { ErrorStringFromException } from '../utils/utils';

export enum OneDriveUploadTaskResultStatus {
    Completed,
    Canceled
}

export interface IOneDriveUploadTaskResult {
    status: OneDriveUploadTaskResultStatus,
    oneDriveItem?: IOneDriveItem
}

// Used to keep track of the upload state inside of OneDriveUploadTask.
enum OneDriveUploadTaskState {
    Default, // Default state. Ready to upload.
    Uploading, // Upload is in progress.
    Canceled // Upload was canceled.
}

export class OneDriveUploadTask {
    private uploadTaskState: OneDriveUploadTaskState = OneDriveUploadTaskState.Default;
    private authProvider: IAuthenticationProvider;
    private graphUploadTask: OneDriveLargeFileUploadTask | null = null;
    private folderName: string;
    private file: File;

    constructor(authProvider: IAuthenticationProvider, folderName: string, file: File) {
        this.authProvider = authProvider;
        this.folderName = folderName;
        this.file = file;
    }

    // Need this method because TypeScript doesn't realize that this.uploadState could have changed after an await statement.
    private GetUploadState() {
        return this.uploadTaskState;
    }

    private async CreateGraphUploadTask() {
        // There's a bug in the Microsoft Graph JavaScript SDK where the telemetry handler middleware is causing
        // OneDriveLargeFileUploadTask to not work with personal Microsoft accounts (https://github.com/microsoftgraph/msgraph-sdk-javascript/issues/265)
        // As a temporary workaround, disable the telemetry handler middleware when doing file upload.
        const graphClient = Client.initWithMiddleware({
            middleware: GraphClientInitOptions.GetDefaultMiddlewareChain(this.authProvider, false /*enableTelemetryHandler*/)
        });

        const options = {
            path: '/' + this.folderName,
            fileName: this.file.name
        };

        return OneDriveLargeFileUploadTask.create(graphClient, this.file, options);
    }

    public async UploadFile(): Promise<IOneDriveUploadTaskResult> {
        LogEvents.ServiceGraphOneDriveUpload.Start();
        if (this.GetUploadState() != OneDriveUploadTaskState.Default) {
            const responseText: string = 'Called UploadFile on a previously used upload task.';
            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: responseText, isFinalResponse: true});
            throw new Error(responseText);
        }

        this.uploadTaskState = OneDriveUploadTaskState.Uploading;

        try {
            this.graphUploadTask = await this.CreateGraphUploadTask();
        } catch (error) {
            if (this.GetUploadState() != OneDriveUploadTaskState.Canceled) {
                LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Creating upload task failed with exception: " + ErrorStringFromException(error), isFinalResponse: true });
                throw error;
            }
        }

        // If the upload was canceled while we were creating the graph upload task, make sure we cancel the task.
        if (this.GetUploadState() == OneDriveUploadTaskState.Canceled) {
            if (this.graphUploadTask) {
                await this.graphUploadTask.cancel();
            }

            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Canceled", isFinalResponse: true});
            return { status: OneDriveUploadTaskResultStatus.Canceled };
        }

        let response = null;

        try {
            response = await this.graphUploadTask.upload();
        } catch (error) {
            // There may be pending upload requests that fail after we cancel, ignore these
            // and only throw if we haven't been canceled.
            if (this.GetUploadState() != OneDriveUploadTaskState.Canceled) {
                LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Uploading failed with exception: " + ErrorStringFromException(error), isFinalResponse: true });
                throw error;
            }
        }

        // Upload was canceled while uploading
        if (this.GetUploadState() == OneDriveUploadTaskState.Canceled) {
            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Canceled", isFinalResponse: true});
            return { status: OneDriveUploadTaskResultStatus.Canceled };
        }

        if (response == null) {
            const responseText: string = 'Response was null in non-canceled upload';
            LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: responseText, isFinalResponse: true});
            throw new Error(responseText);
        }

        LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: true, message: "Upload successful", isFinalResponse: true});
        return { status: OneDriveUploadTaskResultStatus.Completed, 
                oneDriveItem: { driveId: response.parentReference.driveId, itemId: response.id  } };
    }

    public async Cancel(): Promise<void> {
        LogEvents.ServiceGraphOneDriveUpload.AddResponse({ isSuccessful: false, message: "Canceled", isFinalResponse: true});
        this.uploadTaskState = OneDriveUploadTaskState.Canceled;

        // Cancel upload task if it's been created.
        if (this.graphUploadTask != null) {
            try {
                await this.graphUploadTask.cancel();
                this.graphUploadTask = null;
            } catch (error) {
                // Ignore graph errors during cancellation.
            }
        }

        return;
    }
}